import { lazy, Suspense } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { SessionProvider, useSession } from './session';
import { shouldRetry } from './api/hooks';
import Shell from './components/Shell';
import Login from './screens/Login';
import Register from './screens/Register';
import Dashboard from './screens/Dashboard';
import Sites from './screens/Sites';
import Inventory from './screens/Inventory';
import Managers from './screens/Managers';
import Owners from './screens/Owners';
import Billing from './screens/Billing';
import { Loading } from './components/States';

// Recharts is most of the bundle, and only one screen needs it. PRODUCT.md says
// the audience is on a bad Indian mobile link, so the dashboard — the screen they
// open first and most — must not pay to download five charts it never draws.
const Analytics = lazy(() => import('./screens/Analytics'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: shouldRetry,
      refetchOnWindowFocus: false,
      // A dashboard read a few times a day does not need real-time push;
      // stale-while-revalidate on a bad link is what actually helps (§9).
      staleTime: 30_000,
    },
  },
});

function Routed() {
  const { user } = useSession();

  // Signed out, there are two routes rather than one screen — the login form
  // cannot detect "you have no company yet" without weakening the defence that
  // makes /login answer identically for a wrong password and a missing account,
  // so registration is a place you can GO rather than somewhere you are sent.
  if (!user) {
    return (
      <Routes>
        <Route path="/register" element={<Register />} />
        <Route path="*" element={<Login />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route element={<Shell />}>
        <Route index element={<Dashboard />} />
        <Route
          path="analytics"
          element={<Suspense fallback={<Loading what="the charts" />}><Analytics /></Suspense>}
        />
        <Route path="sites" element={<Sites />} />
        <Route path="inventory" element={<Inventory />} />
        <Route path="managers" element={<Managers />} />
        <Route path="owners" element={<Owners />} />
        <Route path="billing" element={<Billing />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <SessionProvider>
        <BrowserRouter><Routed /></BrowserRouter>
      </SessionProvider>
    </QueryClientProvider>
  );
}
